import {useEffect, useState} from 'react'
import {useRouter} from 'next/router'
import _ from 'lodash'
// Contexts
import {useCombinedAuthStatus} from '../../../hooks/use-combined-auth-status'
// Hooks
import {useRouteParams} from '../../../hooks/use-route-params'
// Components
import EntryImagesModal from '../../modals/entry-images-modal'
import PhotoFlowEntry from './photo-flow-entry'
import PhotoGallerySubmissionWizard from '../photo-gallery-submission-wizard'
import PhotoGalleryTopBar from '../photo-gallery-top-bar'
import PhotoGalleryAd from '../photo-gallery-ad'
// Utils
import {accessBallotDisplayType} from '../../../utils/ballot-access'
import {addQueryParam, removeQueryParamByName} from '../../../utils/router-helpers'
// Types
import type {CSSProperties, SyntheticEvent} from 'react'
import type {GalleryEntry, PreviousVote, PhotoGalleryContainerProps} from '../../../types/photo-gallery'
// Styles
import styles from './photo-flow-gallery.module.scss'

type FlowGalleryVariant = 'grid' | 'masonry'

interface Props extends PhotoGalleryContainerProps {
  variant: FlowGalleryVariant
}

const PhotoFlowGallery = ({
  variant,
  customer,
  ballot,
  displayAd,
  currentQuestion,
  currentGroup,
  allFinalists,
  rankDictionary = null,
  currentWindowHeight,
  currentWindowWidth,
  canSubmit,
  submissionIsProcessing,
  hasSponsorshipAd,
  sendModalIsIn = null,
  onPaginate,
  onCastVote,
  onCastSubmission,
  onSelectedAnswerChange,
}: Props) => {
  const {voter} = useCombinedAuthStatus()

  const router = useRouter()
  const {isReady, entryId} = useRouteParams()

  // From currentQuestion
  const [previousVote, setPreviousVote] = useState<PreviousVote | null>(null)
  const [flowEntries, setFlowEntries] = useState<GalleryEntry[]>([])

  const [modalEntry, setModalEntry] = useState<GalleryEntry | null>(null)

  const ballotDisplayType = accessBallotDisplayType(ballot)

  const [gridContainerStyles, setGridContainerStyles] = useState<CSSProperties>({})
  const sendGridContainerStyles = (value: CSSProperties): void => setGridContainerStyles(value)

  const [paymentIsProcessing, setPaymentIsProcessing] = useState(false)
  const sendPaymentIsProcessing = (value: boolean): void => setPaymentIsProcessing(value)

  const generateFlowEntries = (allFinalists: GalleryEntry[], subCategoryId: string | null): GalleryEntry[] => {
    const allQuestionPhotos = _.filter(allFinalists, photo => photo.sub_category_id === subCategoryId)
    let flowEntries = _.cloneDeep(allQuestionPhotos)

    let hasDisplayAd = Boolean(displayAd) && (typeof displayAd === 'object')
    if (hasDisplayAd) {
      hasDisplayAd = (Array.isArray(displayAd) && displayAd.length > 0)
        || (displayAd !== null && Object.keys(displayAd).length > 0)
    }
    if (hasDisplayAd) {
      if (allQuestionPhotos?.length > 2) {
        flowEntries.splice(2, 0, {
          key: 'ad',
          type: 'ad',
          _id: 'ad',
          additional_form_fields: [],
        })
      } else {
        flowEntries.push({
          key: 'ad',
          type: 'ad',
          _id: 'ad',
          additional_form_fields: [],
        })
      }
    }
    return flowEntries
  }

  const [isShowingPreviousVote, setIsShowingPreviousVote] = useState(false)
  const [isLoneEntry, setIsLoneEntry] = useState(true)
  useEffect(() => {
    if (!isReady) return
    const getEntryIndexByEntryId = (entries: GalleryEntry[], entryId: string | string[]): number | undefined => {
      if (!entryId) return

      const movedEntryIndex = _.findIndex(entries, entry => entry._id === entryId)
      return movedEntryIndex
    }
    const getEntryIndexByPhotoImage = (entries: GalleryEntry[], photoImage: string | null): number | undefined => {
      if (!photoImage) return

      const movedEntryIndex = _.findIndex(entries, entry => entry.photo_image === photoImage)
      return movedEntryIndex
    }
    const moveEntryToFrontByEntryIndex = (
      entries: GalleryEntry[], movedEntryIndex: number | undefined, isSelected = false,
    ): GalleryEntry[] => {
      let newFlowEntries = _.cloneDeep(entries)
      let pulledEntry: GalleryEntry | null = null
      if (movedEntryIndex && movedEntryIndex !== -1) {
        [pulledEntry] = _.pullAt(newFlowEntries, movedEntryIndex)
        pulledEntry['isSelected'] = isSelected
        newFlowEntries.unshift(pulledEntry)
      }
      return newFlowEntries
    }

    const previousQuestionVotes = _.filter(voter?.votes, vote => vote.sub_category_id === currentQuestion._id)
    let newSelectedPhotoUrl: string | null = null
    let newPreviousVote: PreviousVote | null = null
    if (previousQuestionVotes.length > 0) {
      const chronologicalPreviousVotes = _.orderBy(previousQuestionVotes, ['voted_time'], ['desc'])
      const mostRecentPreviousVote = _.first(chronologicalPreviousVotes)!
      const currentDateTime = new Date()
      const voteDateTime = new Date(mostRecentPreviousVote.voted_time)
      const currentDateTimeDayArray = [
        currentDateTime.getFullYear(),
        currentDateTime.getMonth() + 1,
        currentDateTime.getDate(),
      ]
      const voteDateTimeDayArray = [
        voteDateTime.getFullYear(),
        voteDateTime.getMonth() + 1,
        voteDateTime.getDate(),
      ]
      const hasVotedToday = _.isEqual(currentDateTimeDayArray, voteDateTimeDayArray)

      const newIsShowingPreviousVote = (
        ballotDisplayType !== 'photo-voting-finalist-announcement'
        && (
          !ballot.general?.one_vote_per_day_turn_on
          || hasVotedToday
        )
      )
      setIsShowingPreviousVote(newIsShowingPreviousVote)
      newSelectedPhotoUrl = newIsShowingPreviousVote ? mostRecentPreviousVote.voted_choice : null

      const newSelectedPhotoName = newIsShowingPreviousVote ? mostRecentPreviousVote.photo_name : null
      const newSelectedPhotoDescription = newIsShowingPreviousVote ? mostRecentPreviousVote.photo_description : null
      newPreviousVote = {
        photo_image: newSelectedPhotoUrl,
        photo_name: newSelectedPhotoName ?? null,
        photo_description: newSelectedPhotoDescription ?? null,
      }
    }
    setPreviousVote(newPreviousVote)

    const allFlowEntries = generateFlowEntries(allFinalists, currentQuestion._id)
    const filteredFlowEntries = _.filter(allFlowEntries, Boolean)
    let newFlowEntries = _.cloneDeep(filteredFlowEntries)

    // Masonry-specific: lone entry detection and column-blocker
    if (variant === 'masonry') {
      const isOnlyCallToAction = (ballotDisplayType === 'photo-submissions-submit') && (newFlowEntries.length === 0)
      const isOnlyAdOrEntry = (ballotDisplayType !== 'photo-submissions-submit') && (newFlowEntries.length <= 1)
      const newIsLoneEntry = isOnlyCallToAction || isOnlyAdOrEntry
      setIsLoneEntry(newIsLoneEntry)

      if (newIsLoneEntry) {
        _.remove(newFlowEntries, entry => entry?.type === 'column-blocker')
        newFlowEntries.push({
          key: 'column-blocker',
          type: 'column-blocker',
          _id: 'column-blocker',
          additional_form_fields: [],
        })
      }
    }

    if (entryId) {
      const entryIndex = getEntryIndexByEntryId(newFlowEntries, entryId)
      const movedEntry = entryIndex !== undefined ? filteredFlowEntries[entryIndex] : undefined
      if (movedEntry) {
        openEntryImagesModal(movedEntry)
      }
      newFlowEntries = moveEntryToFrontByEntryIndex(newFlowEntries, entryIndex)
    }
    if (newPreviousVote) {
      const entryIndex = getEntryIndexByPhotoImage(newFlowEntries, newPreviousVote?.photo_image)
      const isSelected = true
      newFlowEntries = moveEntryToFrontByEntryIndex(newFlowEntries, entryIndex, isSelected)
    }

    if (ballotDisplayType === 'photo-voting-results') {
      newFlowEntries = newFlowEntries.sort((a, b) => {
        if ((!a.rank || a.rank === 0) && (!b.rank || b.rank === 0)) return 0
        if (!a.rank || a.rank === 0) return 1
        if (!b.rank || b.rank === 0) return -1
        return a.rank - b.rank
      })
    }

    setFlowEntries(newFlowEntries)
  }, [
    currentQuestion,
    isReady,
    entryId,
  ])

  const openEntryImagesModal = (entry: GalleryEntry) => {
    addQueryParam(router, {entry_id: entry._id})
    setModalEntry(entry)
    sendModalIsIn?.(true)
  }

  const onCloseEntryImagesModal = () => {
    removeQueryParamByName(router, 'entry_id')

    setModalEntry(null)
    sendModalIsIn?.(false)
  }

  const handleSelectedAnswerChange = (syntheticEvent: SyntheticEvent, flowEntry: GalleryEntry) => {
    let newFlowEntries = _.map(_.cloneDeep(flowEntries), entry => ({...entry, isSelected: false}))
    const selectedEntryIndex = _.findIndex(newFlowEntries, entry => entry.photo_image === flowEntry.photo_image)
    setIsShowingPreviousVote(selectedEntryIndex >= 0)
    newFlowEntries[selectedEntryIndex] = {...flowEntry, isSelected: true}
    setFlowEntries(newFlowEntries)
    onSelectedAnswerChange(syntheticEvent, flowEntry)
  }

  const gap = 16
  const isMasonryLike = variant === 'masonry'
  const topBarVariant = isMasonryLike ? 'flow-masonry' as const : 'flow' as const
  const adVariant = isMasonryLike ? 'flow-masonry' as const : 'flow-grid' as const
  const wizardVariant = isMasonryLike ? 'flow-masonry' as const : 'flow-grid' as const
  const variantClass = styles[`--${variant}`] || ''

  const containerStyles = variant === 'grid'
    ? {
      paddingTop: '72px',
      paddingRight: `${gap}px`,
      paddingBottom: (
        hasSponsorshipAd
          ? `${90 + gap * 0.5}px`
          : `${72 + gap * 0.5}px`
      ),
      paddingLeft: `${gap}px`,
      gridGap: `${gap}px`,
    }
    : {
      paddingTop: `${gap}px`,
      paddingRight: `${gap}px`,
      paddingBottom: hasSponsorshipAd ? '90px' : '72px',
      paddingLeft: `${gap}px`,
      columnGap: `${gap}px`,
    }

  return currentQuestion
    ? (
      <div className={`${styles['__container']} ${isMasonryLike ? styles['__container--masonry'] : ''}`}>
        <PhotoGalleryTopBar
          variant={topBarVariant}
          currentQuestion={currentQuestion}
        />
        <div
          className={`${variantClass} ${isMasonryLike && isLoneEntry ? styles['--has-only-one-entry'] : ''}`}
          style={containerStyles}
        >
          {ballotDisplayType === 'photo-submissions-submit' &&
            <PhotoGallerySubmissionWizard
              variant={wizardVariant}
              customer={customer}
              ballot={ballot}
              question={currentQuestion}
              previousVote={previousVote}
              gridContainerStyles={variant === 'grid' ? gridContainerStyles : undefined}
              canSubmit={canSubmit}
              submissionIsProcessing={submissionIsProcessing}
              paymentIsProcessing={paymentIsProcessing}
              sendPaymentIsProcessing={sendPaymentIsProcessing}
              onCastSubmission={onCastSubmission}
              onPaginate={onPaginate}
              sendModalIsIn={sendModalIsIn!}
            />
          }
          {_.map(flowEntries, flowEntry => {
            if (flowEntry.type === 'option') {
              return <PhotoFlowEntry
                key={flowEntry.key}
                variant={variant}
                ballot={ballot}
                question={currentQuestion}
                entry={flowEntry}
                rankDictionary={rankDictionary}
                previousVote={previousVote}
                ballotDisplayType={ballotDisplayType}
                gridGap={variant === 'grid' ? gap : undefined}
                currentWindowWidth={variant === 'grid' ? currentWindowWidth : undefined}
                gridContainerStyles={variant === 'grid' ? gridContainerStyles : undefined}
                sendGridContainerStyles={variant === 'grid' ? sendGridContainerStyles : undefined}
                masonryGap={isMasonryLike ? gap : undefined}
                isSelected={isShowingPreviousVote && Boolean(flowEntry.isSelected)}
                onSelectedAnswerChange={
                  _.includes(['photo-voting-vote', 'photo-submissions-submit'], ballotDisplayType)
                    ? (syntheticEvent: SyntheticEvent) => handleSelectedAnswerChange(syntheticEvent, flowEntry)
                    : null
                }
                onOpenEntryImagesModal={openEntryImagesModal}
              />
            }
            if (flowEntry.type === 'column-blocker' && isMasonryLike) {
              return (
                <div
                  key={flowEntry.key}
                  className={styles['column-blocker']}
                  style={{marginBottom: `${gap}px`}}
                />
              )
            }
            if (flowEntry.type === 'ad') {
              return <PhotoGalleryAd
                key={flowEntry.key}
                variant={adVariant}
                ballot={ballot}
                group={currentGroup}
                question={currentQuestion}
                displayAd={displayAd}
              />
            }
          })}
        </div>
        {modalEntry &&
          <EntryImagesModal
            entry={{
              ...modalEntry,
              cover_image: modalEntry.cover_image ?? undefined,
            }}
            ballotAdditionalFormFields={ballot.photo_submission_settings?.additional_form_fields}
            currentWindowHeight={currentWindowHeight}
            currentWindowWidth={currentWindowWidth}
            hasVoter={Boolean(voter?._id)}
            onCastVote={voteObject => {
              onCastVote({
                photoImage: voteObject.photoImage ?? '',
                photoName: voteObject.photoName ?? '',
                additionalFormFields: (voteObject.additionalFormFields ?? []).map(f => ({
                  id: f.id ?? '',
                  value: f.value,
                  field_label: f.field_label ?? '',
                })),
              })
            }}
            onCloseEntryImagesModal={onCloseEntryImagesModal}
            ballotDisplayType={ballotDisplayType ?? undefined}
          />
        }
      </div>
    )
    : null
}

export default PhotoFlowGallery
